import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { ApiError } from '../../api/client';
import { AUTH_SESSION_EXPIRED_EVENT, AUTH_TOKENS_UPDATED_EVENT } from '../../api/tokenRefresh';
import {
  getCurrentUser,
  login as loginRequest,
  logout as logoutRequest,
  type CurrentUser,
  type TokenPairResponse,
} from '../../api/auth';
import { clearTokens, loadTokens, saveTokens } from './authStorage';
import { attemptSessionRestore, discardTokens, requireAdminProfile } from './sessionRestore';
import { useSessionKeepAlive } from './useSessionKeepAlive';

// Backoff used while the backend is unreachable during startup. The stored tokens
// are kept the whole time: a failing API must never look like a logout.
const RESTORE_RETRY_DELAYS_MS = [1000, 3000, 8000, 20000];

type LoginPayload = {
  email: string;
  password: string;
};

type AuthContextValue = {
  isAuthenticated: boolean;
  isInitializing: boolean;
  user: CurrentUser | null;
  tokens: TokenPairResponse | null;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [tokens, setTokens] = useState<TokenPairResponse | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  // Set once the session is settled (restored, or provably gone) so the startup
  // retry loop stops racing an interactive login.
  const isSettledRef = useRef(false);

  useEffect(() => {
    // Adopt tokens minted by the keep-alive timer or by another browser tab, so
    // this tab never refreshes an already-rotated token.
    const syncTokensFromStorage = () => {
      const latest = loadTokens();
      if (latest) {
        setTokens(latest);
      }
    };
    const dropSession = () => {
      setTokens(null);
      setUser(null);
    };

    window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, syncTokensFromStorage);
    window.addEventListener('storage', syncTokensFromStorage);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, dropSession);
    return () => {
      window.removeEventListener(AUTH_TOKENS_UPDATED_EVENT, syncTokensFromStorage);
      window.removeEventListener('storage', syncTokensFromStorage);
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, dropSession);
    };
  }, []);

  useEffect(() => {
    let active = true;
    let timer = 0;
    let attempt = 0;

    async function restoreSession(): Promise<void> {
      if (!active || isSettledRef.current) {
        return;
      }

      const outcome = await attemptSessionRestore();
      if (!active || isSettledRef.current) {
        return;
      }

      if (outcome.status === 'restored') {
        isSettledRef.current = true;
        setTokens(outcome.tokens);
        setUser(outcome.user);
        setIsInitializing(false);
        return;
      }

      if (outcome.status === 'anonymous') {
        isSettledRef.current = true;
        setTokens(null);
        setUser(null);
        setIsInitializing(false);
        return;
      }

      // Backend unreachable: retry with backoff, keeping the stored tokens.
      const delay = RESTORE_RETRY_DELAYS_MS[Math.min(attempt, RESTORE_RETRY_DELAYS_MS.length - 1)];
      attempt += 1;
      if (attempt >= RESTORE_RETRY_DELAYS_MS.length) {
        // Stop blocking the UI, but keep retrying in the background.
        setIsInitializing(false);
      }
      timer = window.setTimeout(() => void restoreSession(), delay);
    }

    const restoreNow = () => {
      window.clearTimeout(timer);
      void restoreSession();
    };

    void restoreSession();
    window.addEventListener('online', restoreNow);
    window.addEventListener('focus', restoreNow);

    return () => {
      active = false;
      window.clearTimeout(timer);
      window.removeEventListener('online', restoreNow);
      window.removeEventListener('focus', restoreNow);
    };
  }, []);

  useSessionKeepAlive(tokens?.access_token ?? null);

  const value: AuthContextValue = {
    isAuthenticated: Boolean(tokens && user),
    isInitializing,
    user,
    tokens,
    async login(payload) {
      const nextTokens = await loginRequest(payload);
      let currentUser: CurrentUser;
      try {
        currentUser = requireAdminProfile(await getCurrentUser(nextTokens.access_token));
      } catch (error) {
        await discardTokens(nextTokens);
        setTokens(null);
        setUser(null);
        throw error;
      }
      isSettledRef.current = true;
      saveTokens(nextTokens);
      setTokens(nextTokens);
      setUser(currentUser);
    },
    async logout() {
      isSettledRef.current = true;
      if (tokens?.refresh_token) {
        try {
          await logoutRequest(tokens.refresh_token);
        } catch {
          // Clear local session even if backend logout fails.
        }
      }
      clearTokens();
      setTokens(null);
      setUser(null);
    },
    async refreshCurrentUser() {
      const stored = loadTokens() ?? tokens;
      if (!stored) {
        return;
      }
      try {
        const currentUser = requireAdminProfile(await getCurrentUser(stored.access_token));
        setUser(currentUser);
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) {
          await discardTokens(stored);
          setTokens(null);
          setUser(null);
        }
        throw error;
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
